import { useNavigate, useParams } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { pink } from "@mui/material/colors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthLibMSALTokenAcquisitionError, useMSALPayload } from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { FilledButton, GeneralChip, StatusChip, Typography, useSnackbar } from "@initia-digital/component-library";
import { IPrestartStatus } from "@initia-digital/types/work-and-safety"

import { patchPrestart } from "../api/mutate";
import { useIsAdmin, usePlanDetail } from "../hooks";
import { fetchJobDetail } from "../api/query";
import { CONFIG } from "../config/const";

const { SF_URL } = CONFIG;

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


export const PrestartHeader = ({
    id, status, leadingTeam
}: {
    id?: string, status: IPrestartStatus, leadingTeam?: boolean
}) => {
    // --------------------------------
    const params = useParams();
    const { jobId, planId, prestartId } = params;
    const isCreate = !baseDefinedAndNotNullNarrowing(prestartId);
    let prestartLabel = 'Take-5';
    if (leadingTeam) {
        prestartLabel = 'Prestart meeting';
    }

    const navigate = useNavigate();

    const authDetail = useMSALPayload();

    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    const isAdmin = useIsAdmin(authDetail, true);

    // PLAN DETAIL
    const { planDetailData } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));
    const { title: planTitle } = planDetailData ?? { title: undefined };

    // JOB DETAIL
    const { data: jobDetail } = useQuery({
        queryKey: ['job', jobId],
        queryFn: () => fetchJobDetail(authDetail, jobId!),
        enabled: baseDefinedAndNotNullNarrowing(jobId),
        refetchOnWindowFocus: true,
        retry: 2
    });
    const { jobUUID, jobName, client } = jobDetail ?? { jobUUID: undefined, jobName: undefined, client: undefined };

    // MUTATIONS
    const queryClient = useQueryClient();
    const patchPrestartMutation = useMutation({
        mutationFn: patchPrestart,
        onSuccess: (data) => {
            // ---------------------
            queueSnackbar(`The prestart has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['prestart', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'prestart'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPrestartList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating prestart', { error });
            }
            logger.error('Error updating prestart');
            queueSnackbar('Prestart could not be updated', 'error', 'Error saving prestart', 3000, '1rem');
        }
    });

    return (
        <Box
            display="flex" flexDirection="column"
        >
            <Box
                display="flex" flexDirection="column" alignItems="flex-start"
            >
                <Box
                    display="flex" flexDirection="row" alignItems="center"
                    bgcolor={status !== 'COMPLETED' ? pink[100] : theme.palette.primary.main}
                    borderRadius="0px 10px 10px 0px"
                    pl="5px" pr="8px"
                >
                <Typography
                    fontSize={"0.8rem"}
                    fontWeight={300}
                    color={"black"}
                >
                    {isCreate ? `Save to create a new ${prestartLabel === 'Prestart meeting' ? prestartLabel.toLowerCase() : prestartLabel}` : `Save to update this ${prestartLabel.toLowerCase()}`}
                </Typography>
                </Box>
            </Box>
            <Box
                display="flex" flexDirection="column"
            >
                <Box
                    display="flex" flexDirection="row" alignItems="center"
                    mt={isSmallerThanSm ? "10px" : "0px"}
                >
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        mr="10px"
                    >
                        <Typography
                            fontSize={isSmallerThanSm ? "0.9rem" : "1.2rem"}
                            fontWeight={700}
                            color={theme.palette.primary.contrastText}
                        >
                            {baseDefinedAndNotNullNarrowing(jobName) ? `${prestartLabel} for ${jobName}` : prestartLabel}
                        </Typography>
                    </Box>
                    <StatusChip
                        type="prestart"
                        id={baseDefinedAndNotNullNarrowing(id) ? id : ''}
                        status={status}
                        isAdmin={isAdmin}
                        handleStatusChange={isCreate || status === 'COMPLETED' ? undefined : (prestartId, status, notify = false) => {
                            // ----------------------------------------
                            patchPrestartMutation.mutateAsync({
                                authDetail,
                                prestartId,
                                patches: [
                                    {
                                        "op": "replace",
                                        "path": "/status",
                                        "value": status
                                    }
                                ],
                                notify
                            });
                        }}
                    />
                </Box>                
                {
                    baseDefinedAndNotNullNarrowing(jobDetail)
                    &&
                    <Box
                        display="flex"
                        flexDirection="column"
                        mt="-4px"
                        ml={isSmallerThanMd ? 0 : "4px"}
                    >
                        <Box>
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                {client}
                            </Typography>
                        </Box>
                        <Box>
                            <GeneralChip
                                label="Open in Salesforce"
                                href={`${SF_URL}/${jobUUID}`}
                                size="vsmall"
                                backgroundColor={"#3bbff7"}
                                color="white"
                                clickable
                                newTab={true}
                            />
                        </Box>
                    </Box>
                }    
                <Box
                    display="flex"
                    flexDirection="column"
                    mt='10px'
                    ml={isSmallerThanMd ? 0 : "4px"}
                >
                    <Box
                        mr="20px"
                    >
                        <Typography
                            fontSize={isSmallerThanSm ? "0.9rem" : "1rem"}
                            fontWeight={700}
                            color={theme.palette.secondary.main}
                        >
                            {baseDefinedAndNotNullNarrowing(planTitle) && planTitle.trim() !== '' ? planTitle : 'Untitled work plan'}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                    >
                        <FilledButton
                            key={`view-plan-${planId}`}
                            size="small"
                            tooltipTitle="View plan"
                            onClick={() => {
                                // ---------------------
                                navigate(`/job/${jobId}/view-plan/${planId}`);
                                window.scrollTo(0, 0);
                            }}
                        >
                            View Plan
                        </FilledButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}